import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardPaste, ImagePlus, ScanText, Loader2, Gauge, Check, X, Wand2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { readClipboardImageFile, imageFileFromPasteEvent } from "@/lib/clipboardImage";

const bar = (label, val, testid) => (
  <div className="space-y-1" data-testid={testid}>
    <div className="flex justify-between text-[11px] font-medium text-white/70"><span>{label}</span><span className="text-white">{val}%</span></div>
    <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400" style={{ width: `${val}%` }} /></div>
  </div>
);

const chip = (t, cls, k) => <span key={k} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{t}</span>;

export default function JobDescriptionPanel({ cv, onChange }) {
  const jd = cv.job_description || { text: "", images: [] };
  const analysis = cv.ai_analysis;
  const [ocrBusy, setOcrBusy] = useState(false);
  const [anBusy, setAnBusy] = useState(false);
  const [tlBusy, setTlBusy] = useState(false);
  const [proposals, setProposals] = useState(null);

  const setJd = (text) => onChange({ ...cv, job_description: { ...jd, text } });

  const pasteText = async () => {
    try { const t = await navigator.clipboard.readText(); if (!t) return toast.message("Clipboard is empty."); setJd((jd.text ? jd.text + "\n" : "") + t); toast.success("Pasted from clipboard"); }
    catch { toast.error("Clipboard access blocked — paste directly with Ctrl/Cmd+V."); }
  };

  const ocrFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setOcrBusy(true);
    try {
      const r = await api.cvOcr(list);
      if (!r.text?.trim()) return toast.message("No text detected in the image(s).");
      setJd((jd.text ? jd.text + "\n\n" : "") + r.text);
      toast.success(`Extracted text from ${list.length} image${list.length === 1 ? "" : "s"} — review & edit below`);
    } catch (e) { toast.error(e?.response?.data?.detail || "OCR failed."); } finally { setOcrBusy(false); }
  };

  const pasteImage = async () => {
    try { const f = await readClipboardImageFile(); await ocrFiles([f]); }
    catch (e) { toast.error(e.message || "Couldn't read image from clipboard."); }
  };

  const onPasteEvent = (e) => {
    try { const f = imageFileFromPasteEvent(e); if (f) { e.preventDefault(); ocrFiles([f]); } } catch (err) { toast.error(err.message); }
  };

  const analyze = async () => {
    if (!jd.text.trim()) return toast.error("Paste a job description first.");
    setAnBusy(true);
    try { const a = await api.cvAnalyzeJd(jd.text, cv); onChange({ ...cv, ai_analysis: a }); toast.success("Analysis ready"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed."); } finally { setAnBusy(false); }
  };

  const tailor = async () => {
    if (!analysis) return toast.error("Analyze the job description first.");
    setTlBusy(true);
    try { const p = await api.cvTailor(jd.text, cv, analysis); setProposals(p); toast.success("Review the suggested changes below"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Tailoring failed."); } finally { setTlBusy(false); }
  };

  const applySummary = () => { onChange({ ...cv, summary: proposals.summary.suggested }); setProposals({ ...proposals, summary: null }); toast.success("Summary updated"); };
  const applyExp = (p) => {
    onChange({ ...cv, experience: (cv.experience || []).map((e) => e.id === p.id ? { ...e, description: p.suggested } : e) });
    setProposals({ ...proposals, experiences: proposals.experiences.filter((x) => x !== p) });
    toast.success("Experience updated");
  };

  const m = analysis?.match;

  return (
    <div className="space-y-3" data-testid="cv-jd-panel" onPaste={onPasteEvent}>
      <div className="flex flex-wrap gap-2">
        <button onClick={pasteText} data-testid="cv-jd-paste-text" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:border-white/30"><ClipboardPaste size={13} /> Paste text</button>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:border-white/30" data-testid="cv-jd-upload">
          <ImagePlus size={13} /> Upload image(s)
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => ocrFiles(e.target.files)} data-testid="cv-jd-upload-input" />
        </label>
        <button onClick={pasteImage} data-testid="cv-jd-paste-image" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:border-white/30"><ScanText size={13} /> Paste image → OCR</button>
        {ocrBusy && <span className="inline-flex items-center gap-1 text-[12px] text-emerald-300"><Loader2 size={13} className="animate-spin" /> Extracting…</span>}
      </div>

      <Textarea value={jd.text} onChange={(e) => setJd(e.target.value)} rows={7} placeholder="Paste the full job description here (or paste screenshots above and we'll OCR them for you to review)." data-testid="cv-jd-text"
        className="border-white/10 bg-black/30 text-[13px] leading-relaxed text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40" />

      <div className="flex gap-2">
        <button onClick={analyze} disabled={anBusy} data-testid="cv-jd-analyze" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-[13px] font-bold text-black hover:bg-emerald-400 disabled:opacity-50">{anBusy ? <Loader2 size={15} className="animate-spin" /> : <Gauge size={15} />} Analyze Job Description</button>
        {analysis && <button onClick={tailor} disabled={tlBusy} data-testid="cv-jd-tailor" className="inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-400/50 bg-emerald-400/10 px-3 py-2 text-[13px] font-bold text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-50">{tlBusy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Tailor My CV</button>}
      </div>

      {m && (
        <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="cv-ats-panel">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-bold text-white">ATS Readiness (estimate)</span>
            <span data-testid="cv-ats-score" className="text-2xl font-black text-emerald-400">{m.ats_readiness}%</span>
          </div>
          <p className="text-[11px] text-white/50">An estimate to guide you — not a guaranteed ATS score.</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {bar("Keyword Match", m.keyword_match, "cv-ats-keyword")}
            {bar("Skills Match", m.skills_match, "cv-ats-skills")}
            {bar("Experience Relevance", m.experience_relevance, "cv-ats-exp")}
            {bar("Job Title Alignment", m.job_title_alignment, "cv-ats-title")}
            {bar("Formatting Readiness", m.formatting_readiness, "cv-ats-format")}
          </div>
          {!!(m.missing_keywords || []).length && (
            <div data-testid="cv-ats-missing">
              <div className="mb-1 flex items-center gap-1 text-[12px] font-semibold text-amber-300"><AlertTriangle size={13} /> Missing / weak keywords</div>
              <div className="flex flex-wrap gap-1.5">{m.missing_keywords.map((k, i) => chip(k, "bg-amber-400/15 text-amber-200", i))}</div>
              <p className="mt-1.5 text-[11px] text-white/45">Consider adding these only if you genuinely have this experience.</p>
            </div>
          )}
          {!!(m.matched_keywords || []).length && (
            <div><div className="mb-1 text-[12px] font-semibold text-emerald-300">Matched keywords</div><div className="flex flex-wrap gap-1.5">{m.matched_keywords.map((k, i) => chip(k, "bg-emerald-400/15 text-emerald-200", i))}</div></div>
          )}
          {m.notes && <p className="text-[12px] leading-relaxed text-white/60">{m.notes}</p>}
        </div>
      )}

      {proposals && (
        <div className="space-y-2 rounded-xl border border-emerald-400/30 bg-emerald-400/[0.04] p-3" data-testid="cv-tailor-proposals">
          <div className="text-[13px] font-bold text-white">Suggested changes — review before applying</div>
          {proposals.summary?.suggested && (
            <div className="rounded-lg border border-white/10 bg-black/30 p-2.5" data-testid="cv-tailor-summary">
              <div className="mb-1 text-[11px] font-semibold uppercase text-white/50">Professional Summary</div>
              <div className="whitespace-pre-wrap text-[12.5px] text-emerald-100">{proposals.summary.suggested}</div>
              <div className="mt-2 flex gap-2">
                <button onClick={applySummary} data-testid="cv-tailor-summary-apply" className="inline-flex items-center gap-1 rounded bg-emerald-500 px-2 py-1 text-[11px] font-bold text-black"><Check size={12} /> Apply</button>
                <button onClick={() => setProposals({ ...proposals, summary: null })} className="inline-flex items-center gap-1 rounded border border-white/15 px-2 py-1 text-[11px] font-semibold text-white/70"><X size={12} /> Reject</button>
              </div>
            </div>
          )}
          {(proposals.experiences || []).map((p, i) => (
            <div key={i} className="rounded-lg border border-white/10 bg-black/30 p-2.5" data-testid={`cv-tailor-exp-${i}`}>
              <div className="mb-1 text-[11px] font-semibold uppercase text-white/50">{(cv.experience || []).find((e) => e.id === p.id)?.job_title || "Experience"} — description</div>
              <div className="whitespace-pre-wrap text-[12.5px] text-emerald-100">{p.suggested}</div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => applyExp(p)} data-testid={`cv-tailor-exp-apply-${i}`} className="inline-flex items-center gap-1 rounded bg-emerald-500 px-2 py-1 text-[11px] font-bold text-black"><Check size={12} /> Apply</button>
                <button onClick={() => setProposals({ ...proposals, experiences: proposals.experiences.filter((x) => x !== p) })} className="inline-flex items-center gap-1 rounded border border-white/15 px-2 py-1 text-[11px] font-semibold text-white/70"><X size={12} /> Reject</button>
              </div>
            </div>
          ))}
          {!!(proposals.consider_adding || []).length && (
            <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.06] p-2.5">
              <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-amber-300"><AlertTriangle size={12} /> Consider adding (only if genuinely true)</div>
              <div className="flex flex-wrap gap-1.5">{proposals.consider_adding.map((k, i) => chip(k, "bg-amber-400/15 text-amber-200", i))}</div>
            </div>
          )}
          {proposals.notes && <p className="text-[12px] text-white/60">{proposals.notes}</p>}
        </div>
      )}
    </div>
  );
}
